import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  AdjustGeneralStockInput,
  AdjustGeneralStockResponse,
  CreateGeneralStockInput,
  CreateGeneralStockResponse,
  DeleteGeneralStockResponse,
  GeneralStockTransactionRow,
  ListGeneralStockTransactionsInput,
  UpdateGeneralStockInput,
  UpdateGeneralStockResponse,
} from 'services/interface/generalStock.interface'
import {
  adjustGeneralStock,
  createGeneralStock,
  deleteGeneralStock,
  listGeneralStockTransactions,
  updateGeneralStock,
} from 'services/productApi'

const invalidateInventory = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['inventoryByFactory'] })
}

export const useCreateGeneralStock = (): UseMutationResult<
  CreateGeneralStockResponse,
  Error,
  CreateGeneralStockInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateGeneralStockInput) => createGeneralStock(input),
    onSuccess: () => invalidateInventory(queryClient),
  })
}

export const useUpdateGeneralStock = (): UseMutationResult<
  UpdateGeneralStockResponse,
  Error,
  UpdateGeneralStockInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateGeneralStockInput) => updateGeneralStock(input),
    onSuccess: () => invalidateInventory(queryClient),
  })
}

export const useDeleteGeneralStock = (): UseMutationResult<
  DeleteGeneralStockResponse,
  Error,
  string,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteGeneralStock(id),
    onSuccess: () => invalidateInventory(queryClient),
  })
}

export const useGeneralStockTransactions = (
  input: ListGeneralStockTransactionsInput,
  enabled = true
): { rows: GeneralStockTransactionRow[]; total: number; isFetching: boolean } => {
  const { data, isFetching } = useQuery({
    queryKey: ['generalStockTransactions', input.stock_id, input.page, input.limit],
    queryFn: () => listGeneralStockTransactions(input),
    enabled: enabled && !!input.stock_id,
  })
  return {
    rows: data?.data?.rows ?? [],
    total: data?.data?.total ?? 0,
    isFetching,
  }
}

export const useAdjustGeneralStock = (): UseMutationResult<
  AdjustGeneralStockResponse,
  Error,
  AdjustGeneralStockInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AdjustGeneralStockInput) => adjustGeneralStock(input),
    onSuccess: () => invalidateInventory(queryClient),
  })
}
